<template>
  <div class="flex flex-col gap-2">
    <div :class="wrapperClasses">
      <span class="text-2xl text-gray-500 leading-none mr-1 select-none"
        >$</span
      >
      <input
        ref="inputRef"
        :value="displayValue"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        :disabled="disabled"
        :data-cy="dataCy"
        class="flex-1 min-w-0 border-0 bg-transparent p-0 text-[40px] font-semibold tabular-nums tracking-tight text-gray-900 focus:outline-none focus:ring-0 disabled:text-gray-400"
        @focus="handleFocus"
        @input="handleInput"
        @blur="handleBlur"
        @keydown.enter.prevent="emitBlurAndSubmit"
      />
    </div>
    <div
      v-if="hint || $slots.hint"
      class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600"
    >
      <slot name="hint">{{ hint }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: number;
    tone?: "pay" | "refund";
    disabled?: boolean;
    hint?: string;
    dataCy?: string;
  }>(),
  {
    tone: "pay",
    disabled: false,
    hint: "",
    dataCy: undefined
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", cents: number): void;
  (e: "submit"): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const editing = ref(false);
const rawText = ref("");

const displayValue = computed(() => {
  if (editing.value) return rawText.value;
  return formatDollars(props.modelValue);
});

const wrapperClasses = computed(() => {
  const base =
    "flex items-baseline border-b-2 pb-1 transition-colors duration-150";
  const focus =
    props.tone === "refund"
      ? "border-gray-900 focus-within:border-red-600"
      : "border-gray-900 focus-within:border-blue-600";
  return `${base} ${focus}`;
});

function formatDollars(cents: number): string {
  const n = (cents || 0) / 100;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function parseToCents(text: string): number {
  const cleaned = text.replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const dollars = parseFloat(cleaned);
  if (isNaN(dollars)) return 0;
  return Math.round(dollars * 100);
}

function handleFocus() {
  editing.value = true;
  rawText.value = ((props.modelValue || 0) / 100).toFixed(2);
  // select after the value swap settles
  requestAnimationFrame(() => inputRef.value?.select());
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  rawText.value = target.value;
  emit("update:modelValue", parseToCents(target.value));
}

function handleBlur() {
  editing.value = false;
  rawText.value = "";
}

function emitBlurAndSubmit() {
  inputRef.value?.blur();
  emit("submit");
}

watch(
  () => props.modelValue,
  (next) => {
    if (!editing.value) return;
    const currentCents = parseToCents(rawText.value);
    if (currentCents !== next) {
      rawText.value = ((next || 0) / 100).toFixed(2);
    }
  }
);
</script>
